import { MapPinCheck, Info, Video } from 'lucide-react';

interface ConfirmationLocationProps {
	serviceModality: Professionals.ServiceModality;
	clinicAddress: Clinics.Address | null;
	typeLabel: string;
}

const MobileTypeSection = ({
	typeLabel,
	variant = 'neutral',
}: {
	typeLabel: string;
	variant?: 'neutral' | 'emerald';
}) => (
	<div>
		<div
			className={
				variant === 'emerald'
					? 'mb-3 flex items-center gap-2 text-emerald-600/80'
					: 'mb-3 flex items-center gap-2 text-gray-400'
			}
		>
			<Video size={16} />
			<span className="text-[10px] font-bold uppercase tracking-wider">
				Tipo
			</span>
		</div>
		<p
			className={
				variant === 'emerald'
					? 'text-sm font-bold text-emerald-900'
					: 'text-sm font-bold text-gray-800'
			}
		>
			{typeLabel}
		</p>
	</div>
);

const ConfirmationLocationContainer = ({
	serviceModality,
	clinicAddress,
	typeLabel,
}: ConfirmationLocationProps) => {
	return (
		<div className="w-full space-y-3">
			{serviceModality === 'FULL_REMOTE' && (
				<>
					<div className="md:hidden">
						<div className="w-full space-y-4 rounded-2xl border border-emerald-100 bg-emerald-50/50 px-5 py-4">
							<MobileTypeSection
								typeLabel={typeLabel}
								variant="emerald"
							/>
							<div className="h-px bg-emerald-200/80" />
							<div>
								<div className="mb-3 flex items-center gap-2 text-emerald-600/80">
									<Info size={16} />
									<span className="text-[10px] font-bold uppercase tracking-wider">
										Informações importantes
									</span>
								</div>
								<p className="text-xs leading-relaxed text-emerald-700 opacity-80">
									A consulta acontecerá aqui na Plumaa.
									Certifique-se de estar em um local
									silencioso e com boa conexão de internet
									cerca de 10 minutos antes. No horário
									marcado, entre pelo menu{' '}
									<span className="font-semibold">
										Início
									</span>{' '}
									e toque em{' '}
									<span className="font-semibold">
										Acessar
									</span>{' '}
									na sua consulta.
								</p>
							</div>
						</div>
					</div>

					<div className="hidden w-full justify-center md:flex">
						<div className="flex w-full max-w-xl items-start gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/50 px-6 py-5">
							<div className="mt-1 flex aspect-square h-8 w-8 items-center justify-center rounded-full bg-emerald-600/10">
								<Info className="h-4 w-4 text-emerald-700" />
							</div>
							<div className="space-y-1">
								<p className="text-xs font-semibold text-emerald-900 md:text-sm">
									Informações Importantes
								</p>
								<p className="text-xs leading-relaxed text-emerald-700 opacity-80 md:text-xs">
									A consulta acontecerá aqui na Plumaa.
									Certifique-se de estar em um local
									silencioso e com boa conexão de internet
									cerca de 10 minutos antes. No horário
									marcado, entre pelo menu{' '}
									<span className="font-semibold">
										Início
									</span>{' '}
									e toque em{' '}
									<span className="font-semibold">
										Acessar
									</span>{' '}
									na sua consulta.
								</p>
							</div>
						</div>
					</div>
				</>
			)}

			{serviceModality === 'FULL_PRESENCIAL' && (
				<>
					<div className="rounded-2xl border border-gray-100 bg-gray-50 p-5 md:hidden">
						<MobileTypeSection typeLabel={typeLabel} />
						<div className="my-4 h-px bg-gray-200" />
						<div className="flex items-start gap-3">
							<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan-800/5 text-cyan-900">
								<MapPinCheck className="h-5 w-5" />
							</div>
							<div className="min-w-0 space-y-1">
								<p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
									Local da consulta
								</p>
								{clinicAddress ? (
									<p className="text-xs leading-relaxed text-zinc-500">
										{clinicAddress.street_name},{' '}
										{clinicAddress.number} -{' '}
										{clinicAddress.district_name},{' '}
										{clinicAddress.city} -{' '}
										{clinicAddress.state}
									</p>
								) : (
									<p className="text-sm font-semibold text-zinc-900">
										Endereço informado pelo profissional
									</p>
								)}
							</div>
						</div>
					</div>

					<div className="hidden items-start gap-3 md:flex">
						<div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-800/5 text-cyan-900">
							<MapPinCheck className="h-5 w-5" />
						</div>
						<div className="space-y-1">
							<p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
								Local da consulta
							</p>
							<p className="text-sm font-semibold text-zinc-900">
								Atendimento presencial
							</p>
							{clinicAddress && (
								<p className="text-xs leading-relaxed text-zinc-500">
									{clinicAddress.street_name},{' '}
									{clinicAddress.number} -{' '}
									{clinicAddress.district_name},{' '}
									{clinicAddress.city} - {clinicAddress.state}
								</p>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export { ConfirmationLocationContainer };
